import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {AuthenticationService} from '@services/authentification.service';
import {OauthAuthorizeRequest, OauthClient, OauthService} from '@services/oauth.service';
import {TrackingService} from '@services/tracking.service';

// Demande sauvegardée le temps du login : LoginComponent navigue vers returnUrl.split('?')[0] et perd la query.
const STORAGE_KEY = 'lybox_oauth_request';
const PARAM_KEYS = ['client_id', 'redirect_uri', 'response_type', 'state', 'scope', 'code_challenge', 'code_challenge_method', 'resource'];

@Component({
    selector: 'app-oauth-authorize',
    templateUrl: './oauth-authorize.component.html',
    styleUrls: ['./oauth-authorize.component.scss']
})
export class OauthAuthorizeComponent implements OnInit {
    state: 'loading' | 'invalid' | 'consent' = 'loading';
    client: OauthClient = null;
    request: OauthAuthorizeRequest = null;
    submitting = false;
    error = '';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public authenticationService: AuthenticationService,
        private oauthService: OauthService,
        private trackingService: TrackingService,
    ) {
    }

    ngOnInit() {
        this.request = this.readRequest();
        if (!this.request || !this.request.client_id || !this.request.redirect_uri) {
            this.state = 'invalid';
            return;
        }
        this.oauthService.getClient(this.request.client_id).subscribe(
            client => {
                if (!client || !Array.isArray(client.redirect_uris) || client.redirect_uris.indexOf(this.request.redirect_uri) === -1) {
                    this.state = 'invalid';
                    return;
                }
                this.client = client;
                if (!this.authenticationService.isLogin) {
                    this.goLogin();
                    return;
                }
                this.state = 'consent';
            },
            () => {
                this.state = 'invalid';
            });
    }

    // Lit la query telle quelle ; si elle est vide, restaure la demande mise de côté avant le login.
    private readRequest(): OauthAuthorizeRequest {
        let params: any = this.route.snapshot.queryParams;
        if (Object.keys(params).length === 0) {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            sessionStorage.removeItem(STORAGE_KEY);
            if (!saved) {
                return null;
            }
            try {
                params = JSON.parse(saved);
            } catch (e) {
                return null;
            }
        }
        const request: any = {};
        PARAM_KEYS.forEach(key => {
            if (typeof params[key] === 'string') {
                request[key] = params[key];
            }
        });
        return request;
    }

    private goLogin() {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.request));
        this.router.navigate(['/app/login'], {queryParams: {returnUrl: '/app/oauth/authorize'}});
    }

    switchAccount() {
        this.authenticationService.logout();
        this.goLogin();
    }

    authorize() {
        if (this.state !== 'consent' || this.submitting) {
            return;
        }
        this.trackingService.click('oauth_authorize', this.client.client_name);
        this.submitting = true;
        this.error = '';
        this.oauthService.authorize(this.request).subscribe(
            response => {
                window.location.href = response.redirect_uri;
            },
            (err: HttpErrorResponse) => {
                if (err.status === 401) {
                    this.authenticationService.logout();
                    this.goLogin();
                    return;
                }
                this.submitting = false;
                this.error = (err.error && err.error.error_description) ? err.error.error_description : 'Une erreur est survenue, merci de réessayer.';
            });
    }

    deny() {
        if (this.state !== 'consent' || this.submitting) {
            return;
        }
        this.trackingService.click('oauth_deny', this.client.client_name);
        let url: URL;
        try {
            url = new URL(this.request.redirect_uri);
        } catch (e) {
            this.state = 'invalid';
            return;
        }
        url.searchParams.set('error', 'access_denied');
        if (this.request.state) {
            url.searchParams.set('state', this.request.state);
        }
        window.location.href = url.toString();
    }
}
